import React, { useState, useEffect } from "react";
import { Modal, Form, Card } from "react-bootstrap";
import SigniteButton from "../../../ui-elements/signite-button/signiteButton";
import { ClientsListingTable } from "../../admin-pages/client-listing/clients-listing-table/clientsListingTable";
import { QueryClient, QueryClientProvider } from "react-query";
import { ClientService } from "../../../../services/clientService";
import { prepareClientData } from "../../admin-pages/client-listing/clients-listing-table/clientListingUtils";
import Loader from "react-spinner-loader";
import FolderIcon from '../../../../assets/images/Ic_folder.png';

const ErrorPopup = ({ show, message, onClose }) => {
    if (!show) return null;
    return (
        <div className="custom-alert-overlay">
            <div className="custom-alert-box">
                <div className="custom-alert-content">
                    <p>{message}</p>
                    <button className="custom-alert-button" onClick={onClose}>OK</button>
                </div>
            </div>
        </div>
    );
};

const ConfirmCuratePopup = ({ show, onClose, onConfirm }) => {
    if (!show) return null;
    return (
        <div className="custom-alert-overlay">
            <div className="custom-alert-box">
                <div className="custom-alert-content">
                    <p>Are you sure you want to curate this candidate?</p>
                    <div style={{ display: "flex", gap: "10px", marginTop: "15px", justifyContent: "flex-end" }}>
                        <button className="custom-alert-button" onClick={onClose}>No</button>
                        <button
                            className="custom-alert-button"
                            onClick={onConfirm}
                            style={{ backgroundColor: "#F35824", color: "#fff" }}
                        >
                            Yes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CreateFolderModal = ({ show, onClose, clientId, clientName, candidateId }) => {
    const PAGE_SIZE = 8;
    const [folderPage, setFolderPage] = useState(0);
    const [folderTotalPages, setFolderTotalPages] = useState(0);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newFolderName, setNewFolderName] = useState("");
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [errorPopup, setErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmPopup, setConfirmPopup] = useState(false);

    useEffect(() => {
        const fetchFolders = async () => {
            if (!clientId || !show) return;
            setLoading(true);
            try {
                const resp = await ClientService.getCuratedFolders(
                    clientId,
                    "CURATED",
                    folderPage,
                    PAGE_SIZE
                );
                setFolders(resp?.data?.folders || []);
                const totalCount = resp?.data?.totalCount || 0;
                setFolderTotalPages(Math.ceil(totalCount / PAGE_SIZE));
            } catch (err) {
                console.error("Error loading folders", err);
            }
            setLoading(false);
        };
        fetchFolders();
    }, [clientId, show, folderPage]);

    const handleCreate = async () => {
        if (!newFolderName.trim()) {
            setErrorMessage("Please give a folder name.");
            setErrorPopup(true);
            return;
        }

        setLoading(true);
        try {
            await ClientService.createClientFolder(clientId, newFolderName);
            const updated = await ClientService.getCuratedFolders(
                clientId,
                "CURATED",
                folderPage,
                PAGE_SIZE
            );
            setFolders(updated?.data?.folders || []);
            setNewFolderName("");
        } catch (err) {
            console.error("Error creating folder", err);
            const msg = err?.response?.data?.message || err?.response?.data?.error || "Something went wrong.";
            setErrorMessage(msg);
            setErrorPopup(true);
        }
        setLoading(false);
    };

    const performCurate = async () => {
        setConfirmPopup(false);
        if (!selectedFolderId) return;

        setLoading(true);
        try {
            await ClientService.curateCandidateToFolder(clientId, candidateId, selectedFolderId);
            alert("Candidate curated successfully!");
            onClose();
        } catch (err) {
            console.error("Error curating candidate", err);
            const msg = err?.response?.data?.message || err?.response?.data?.error || "Something went wrong.";
            setErrorMessage(msg);
            setErrorPopup(true);
        }
        setLoading(false);
    };

    const renderFolderPageNumbers = () => {
        const pageNumbers = [];
        const current = folderPage + 1;

        if (folderTotalPages <= 5) {
            for (let i = 1; i <= folderTotalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);

            if (current > 3) {
                pageNumbers.push('...');
            }

            const start = Math.max(2, current - 1);
            const end = Math.min(folderTotalPages - 1, current + 1);

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }

            if (current < folderTotalPages - 2) {
                pageNumbers.push('...');
            }

            pageNumbers.push(folderTotalPages);
        }

        return pageNumbers;
    };

    return (
        <>
            <Modal show={show} onHide={() => { setSelectedFolderId(null); onClose(); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create / Select Folder – {clientName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <Loader show={true} type="spinner" message="Loading..." />
                    ) : (
                        <>
                            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                                <Form.Control
                                    placeholder="Folder Name"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                />
                                <SigniteButton
                                    variant="primary"
                                    disabled={!newFolderName.trim()}
                                    onClick={handleCreate}
                                >
                                    Create
                                </SigniteButton>
                            </div>

                            <Card>
                                <Card.Body>
                                    {folders.length === 0 ? (
                                        <p>No folders found.</p>
                                    ) : (
                                        <table style={{ width: "100%" }}>
                                            <tbody>
                                                {folders.map((f) => (
                                                    <React.Fragment key={f.folderId}>
                                                        <tr
                                                            className={selectedFolderId === f.folderId ? "selected" : ""}
                                                            onClick={() => setSelectedFolderId(f.folderId)}
                                                            style={{
                                                                cursor: "pointer",
                                                                background: selectedFolderId === f.folderId ? "#d9eaff" : "transparent"
                                                            }}
                                                        >
                                                            <td style={{ padding: 8 }}>
                                                                <img src={FolderIcon} alt="folder icon" className="folder-icon" style={{ width: 22, marginRight: 8 }} />
                                                                <span>{f.name}</span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: 0 }}>
                                                                <hr style={{ margin: 0, border: 0, borderBottom: "2px solid #bfbfbf" }} />
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </Card.Body>
                            </Card>

                            {folderTotalPages > 1 && (
                                <div className="d-flex justify-content-center my-3">
                                    <div className="pagination-container">

                                        <button
                                            onClick={() => setFolderPage(0)}
                                            disabled={folderPage === 0}
                                        >
                                            {'<<'}
                                        </button>

                                        <button
                                            onClick={() =>
                                                setFolderPage((prev) => Math.max(prev - 1, 0))
                                            }
                                            disabled={folderPage === 0}
                                        >
                                            {'<'}
                                        </button>

                                        {renderFolderPageNumbers().map((page, index) => (
                                            <button
                                                key={index}
                                                className={`border rounded p-1 ${folderPage + 1 === page ? 'active' : ''
                                                    }`}
                                                onClick={() => {
                                                    if (typeof page === 'number') {
                                                        setFolderPage(page - 1);
                                                    }
                                                }}
                                                disabled={typeof page !== 'number'}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            onClick={() =>
                                                setFolderPage((prev) =>
                                                    Math.min(prev + 1, folderTotalPages - 1)
                                                )
                                            }
                                            disabled={folderPage >= folderTotalPages - 1}
                                        >
                                            {'>'}
                                        </button>

                                        <button
                                            onClick={() => setFolderPage(folderTotalPages - 1)}
                                            disabled={folderPage >= folderTotalPages - 1}
                                        >
                                            {'>>'}
                                        </button>

                                    </div>
                                </div>
                            )}

                            <div style={{ textAlign: "right", marginTop: 15 }}>
                                <SigniteButton
                                    variant="primary"
                                    onClick={() => {
                                        if (!selectedFolderId) {
                                            setErrorMessage("Please select a folder to curate.");
                                            setErrorPopup(true);
                                            setSelectedFolderId(null);
                                            return;
                                        }
                                        setConfirmPopup(true);
                                    }}
                                >
                                    Curate
                                </SigniteButton>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <ErrorPopup
                show={errorPopup}
                message={errorMessage}
                onClose={() => { setErrorPopup(false); setSelectedFolderId(null); }}
            />

            <ConfirmCuratePopup
                show={confirmPopup}
                onClose={() => setConfirmPopup(false)}
                onConfirm={performCurate}
            />
        </>
    );
};

const queryClient = new QueryClient();

const CuratePopup = ({ show, onClose, candidateId }) => {
    const [clientName, setClientName] = useState("");
    const [updatedClientName, setUpdatedClientName] = useState("");
    const [loader, setLoader] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientIdForFolder, setClientIdForFolder] = useState(null);
    const [showFolderModal, setShowFolderModal] = useState(false);

    const searchClient = () => setUpdatedClientName(clientName);

    const setSearchField = (event) => {
        const value = event.target.value;
        setClientName(value);
        if (value === "") setUpdatedClientName("");
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            searchClient();
        }
    };

    const fetchClientList = async ({ pageIndex = 0, pageSize = 10 }) => {
        setLoader(true);
        let resp;
        if (updatedClientName && updatedClientName.length > 0) {
            resp = await ClientService.getAllClients(0, pageSize, updatedClientName);
        } else {
            resp = await ClientService.getAllClients(pageIndex, pageSize);
        }
        setLoader(false);
        return {
            rows: prepareClientData(resp, handleClientSelect, null, null, true),
            pageCount: resp.data.pageCount,
        };
    };

    const handleClientSelect = (row) => {
        const clientId = row.clientId;
        const companyName = row.companyName;

        setSelectedClient(companyName);
        setClientIdForFolder(clientId);
        setShowFolderModal(true);
    };


    return (
        <>
            <Modal show={show} onHide={onClose} size="lg" backdrop="static" keyboard={false}>
                {loader && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0,0,0,0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 20,
                            borderRadius: 6,
                        }}
                    >
                        <Loader show={true} type="spinner" message="Loading..." />
                    </div>
                )}

                <Modal.Header closeButton>
                    <Modal.Title>Client List</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div style={{ marginBottom: 10, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <Form.Control
                            className="mb-2"
                            placeholder="Company Name"
                            value={clientName}
                            onKeyDown={onKeyDown}
                            spellCheck="false"
                            onChange={setSearchField}
                            style={{ maxWidth: 300 }}
                        />
                        <SigniteButton variant="primary" className="mb-2" onClick={searchClient}>
                            Search
                        </SigniteButton>
                    </div>

                    <QueryClientProvider client={queryClient}>
                        <div style={{ minHeight: 300, position: "relative" }}>
                            <ClientsListingTable fetchData={fetchClientList} clientName={updatedClientName} />
                        </div>
                    </QueryClientProvider>
                </Modal.Body>
            </Modal>

            <CreateFolderModal
                show={showFolderModal}
                onClose={() => setShowFolderModal(false)}
                clientId={clientIdForFolder}
                clientName={selectedClient}
                candidateId={candidateId}
            />
        </>
    );
};

export default CuratePopup;